import { isNativeApp } from './nativeApp';

/** Matches HygienistHome `.actionArea` — footer Add Shift shows below this width. */
export const TALENT_COMPACT_LAYOUT_MAX_WIDTH_PX = 767;

type TalentLayoutTarget = Pick<Window, 'innerWidth' | 'addEventListener' | 'removeEventListener'> & {
  matchMedia?: (query: string) => MediaQueryList | undefined;
};

function getTalentLayoutMediaQuery(
  maxWidthPx: number,
  target: TalentLayoutTarget,
): MediaQueryList | null {
  if (typeof target.matchMedia !== 'function') return null;
  const mediaQuery = target.matchMedia(`(max-width: ${maxWidthPx}px)`);
  return mediaQuery && typeof mediaQuery.matches === 'boolean' ? mediaQuery : null;
}

export function isNarrowTalentLayoutViewport(
  maxWidthPx: number = TALENT_COMPACT_LAYOUT_MAX_WIDTH_PX,
  target?: TalentLayoutTarget,
): boolean {
  if (!target && typeof window === 'undefined') return false;
  const resolvedTarget = target || window as any;
  const mediaQuery = getTalentLayoutMediaQuery(maxWidthPx, resolvedTarget);
  if (mediaQuery) return mediaQuery.matches;
  return Number.isFinite(resolvedTarget.innerWidth) && resolvedTarget.innerWidth <= maxWidthPx;
}

export function subscribeToTalentLayoutChanges(
  listener: () => void,
  maxWidthPx: number = TALENT_COMPACT_LAYOUT_MAX_WIDTH_PX,
  target?: TalentLayoutTarget,
): () => void {
  if (!target && typeof window === 'undefined') return () => undefined;
  const resolvedTarget = target || window as any;
  const mediaQuery = getTalentLayoutMediaQuery(maxWidthPx, resolvedTarget);
  if (mediaQuery && typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }
  if (mediaQuery && typeof (mediaQuery as any).addListener === 'function') {
    (mediaQuery as any).addListener(listener);
    return () => (mediaQuery as any).removeListener?.(listener);
  }
  resolvedTarget.addEventListener('resize', listener);
  return () => resolvedTarget.removeEventListener('resize', listener);
}

/** Cordova shell or narrow web — compact header chrome (footer CTAs, icon-only job board). */
export function isTalentCompactLayout(): boolean {
  return isNativeApp() || isNarrowTalentLayoutViewport();
}

/**
 * T2-6334: hide navbar Add Shift when a page renders its own footer CTA on compact layouts.
 */
export function shouldHideTalentHeaderAddShift(
  hideAddShiftOnMobile: boolean,
  isCompactLayout: boolean,
): boolean {
  return hideAddShiftOnMobile && isCompactLayout;
}
